import {Injectable} from '@angular/core';
import {WSMessage} from 'ac-infra';

import {UntilDestroy} from '@ngneat/until-destroy';
import {cloneDeep} from 'lodash';
import {TopologyTreeBase} from './topology-tree-base';
import {NetworkTreeDataBuilderService} from '../data/network-tree-data-builder.service';

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class TopologyTreeService extends TopologyTreeBase {

    readonly ENTITY_TYPE_MAP = {
        tenant: true,
        region: true,
        device: true,
        link: true,
        site: true
    };

    constructor(private networkTreeDataBuilderService: NetworkTreeDataBuilderService) {
        super();
    }

    protected onWSEntitiesUpdate({messageType, entityType, entityTypeName, entitiesIds}: WSMessage) {
        entityType = entityType?.toLowerCase();
        if (!this.ENTITY_TYPE_MAP[entityType]) {
            return;
        }

        switch (messageType) {
            case 'Create': {
                this.createFlatNodes(entitiesIds, entityTypeName);
                break;
            }
            case 'Update': {
                this.updateFlatNodes(entitiesIds, entityTypeName, entityType);
                break;
            }
            case 'Delete': {
                this.deleteFlatNodes(entitiesIds);
                break;
            }
            default : {
                return;
            }
        }
        this.emitTreeNodesUpdate();
    }

    private createFlatArtificial(flatParentNode: any, newNode) {
        const artificialNode = this.networkTreeDataBuilderService.getBaseArtificialParent(flatParentNode.originalEntity, newNode);

        if (!this.flatTreeMap[artificialNode.id]) {
            this.buildFlatTree(this.flatTreeMap, artificialNode, flatParentNode);
            this.addNodeToFlatParentChildren(flatParentNode, artificialNode);
        }

        return this.flatTreeMap[artificialNode.id];
    }

    private addNodeToFlatParent(flatParent, newNode) {
        const needArtificialParent = !!flatParent && this.networkTreeDataBuilderService.needArtificialParent(newNode.entityType, flatParent.originalEntity.entityType);

        flatParent = needArtificialParent ? this.createFlatArtificial(flatParent, newNode) : flatParent;

        this.addNodeToFlatParentChildren(flatParent, newNode);
    }

    private createFlatNodes(entitiesIds: any[], entityTypeName) {
        const newNodes = cloneDeep(this.wsEntitiesService.getEntitiesArray(entityTypeName, entitiesIds));

        newNodes.forEach(newNode => {
            this.buildFlatTree(this.flatTreeMap, newNode);

            const parentId = this.networkTreeDataBuilderService.getNativeParentId(newNode);
            const flatParent = this.flatTreeMap[parentId];

            this.addNodeToFlatParent(flatParent, newNode);

            this.addTenantToUpdateNodesQueue(newNode.tenantId || newNode.id);
        });
    }

    private updateFlatNodes(entitiesIds: any[], entityTypeName, entityType) {
        if (!['tenant', 'region'].includes(entityType)) {
            this.deleteFlatNodes(entitiesIds);
            this.createFlatNodes(entitiesIds, entityTypeName);
            return;
        }

        const newNodes = cloneDeep(this.wsEntitiesService.getEntitiesArray(entityTypeName, entitiesIds));
        newNodes.forEach((newNode) => {
            const oldNode = this.flatTreeMap[newNode.id]?.originalEntity;

            if (!oldNode) {
                return;
            }

            newNode.entityType = entityType;
            newNode.hasChildren = oldNode.hasChildren;

            Object.getOwnPropertyNames(oldNode).forEach((prop) => {
                delete oldNode[prop];
            });
            Object.assign(oldNode, newNode);

            this.addTenantToUpdateNodesQueue(newNode.tenantId || newNode.id);
        });
    }
}
